import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { actions as authActions } from 'features/Auth';
import { actions } from 'features/users/redux';
import { actions as userSettingsActions } from 'features/userSettings';
import parseNumberFromString from 'shared/helpers/parseNumberFromString';

import { amountTypes } from '../data';
import { canPerformFinancialOperationByUserInfo } from '../helpers/canPerformFinancialOperation';

const useDepositModel = () => {
  const dispatch = useDispatch();
  const { userId } = useParams();
  const history = useHistory();

  const {
    username: authUsername,
    balance: authBalance,
    role: authRole,
    currency,
    id: authId,
  } = useSelector(state => state.auth);
  const locale = useSelector(state => state.locale.locale);
  const { userInfo, actionProcessing } = useSelector(state => state.users);
  const savedBonusSettings = useSelector(state => state.userSettings.savedBonusSettings);

  const [amount, setAmount] = useState();
  const [bonusAmount, setBonusAmount] = useState();
  const [bonusPercentAmount, setBonusPercentAmount] = useState();
  const [isBonus, setIsBonus] = useState(false);
  const [rememberBonusSelection, setRememberBonusSelection] = useState(false);
  const initialLoadDone = useRef(false);

  const isCurrentUserLoaded = String(userInfo.id) === userId;
  const isPlayer = userInfo.role === '0';
  const isOwnPlayer = userInfo.parentAgentId === authId;
  const isDirectPlayer = isCurrentUserLoaded && isPlayer && isOwnPlayer;

  const canOperate = useMemo(() => {
    if (!isCurrentUserLoaded) {
      return true;
    }

    return canPerformFinancialOperationByUserInfo({ authId, userInfo });
  }, [isCurrentUserLoaded, authId, userInfo]);

  useEffect(() => {
    if (isCurrentUserLoaded && !canOperate) {
      history.push('/users/all');
    }
  }, [isCurrentUserLoaded, canOperate, history]);

  const resetBonusRemember = useCallback(() => {
    setRememberBonusSelection(false);
    dispatch(userSettingsActions.clearSavedBonusSettings());
  }, [dispatch]);

  useEffect(() => {
    initialLoadDone.current = false;
    setAmount(undefined);
    setBonusAmount(undefined);
    setBonusPercentAmount(undefined);
    setIsBonus(false);
    setRememberBonusSelection(false);
    dispatch(actions.getUserInfo({ id: userId, withParent: true }));
  }, [userId, dispatch]);

  useEffect(() => {
    const hasSavedBonus = savedBonusSettings?.isEnabled;
    const shouldAutoFill = isCurrentUserLoaded && isDirectPlayer && hasSavedBonus && !initialLoadDone.current;

    if (!shouldAutoFill) return;

    initialLoadDone.current = true;
    setIsBonus(true);
    setBonusAmount(savedBonusSettings.bonusAmount || '');
    setBonusPercentAmount(savedBonusSettings.bonusPercentAmount || '');
    setRememberBonusSelection(true);
  }, [isCurrentUserLoaded, isDirectPlayer, savedBonusSettings]);

  const players = useMemo(
    () => [
      {
        username: userInfo?.parentInfo?.username ?? authUsername,
        balance: userInfo?.parentInfo?.balance ?? authBalance,
        role: userInfo?.parentInfo?.role ?? authRole,
        title: locale.from,
      },
      {
        username: userInfo.username,
        balance: userInfo.balance,
        role: userInfo.role,
        title: locale.to,
      },
    ],
    [userInfo, authUsername, authBalance, authRole, locale],
  );

  const onSubmit = useCallback(() => {
    dispatch(
      actions.depositOrWithdrawal({
        amount,
        operation: 0,
        id: userId,
        bonusAmount,
        bonusPercentAmount,
        isBonus,
        callback: () => {
          dispatch(actions.getUserInfo({ id: userId }));
          dispatch(authActions.getBalance());
          history.push('/users/all');
        },
      }),
    );
  }, [amount, bonusAmount, bonusPercentAmount, dispatch, history, isBonus, userId]);

  const changeValue = useCallback(
    e => {
      const value = e?.currentTarget?.value ?? e;

      if (e?.currentTarget) {
        setAmount(() => parseNumberFromString(value));
      } else {
        setAmount(() => {
          // if (!Object.keys(amountTypes).includes(value)) return Number(value);
          return Number(value) + Number(parseNumberFromString(amount));
        });
      }
    },
    [amount],
  );

  const handleIsBonusChange = useCallback(
    value => {
      setIsBonus(value);
      if (!value) {
        setBonusPercentAmount('');
        setBonusAmount('');
        resetBonusRemember();
      }
    },
    [resetBonusRemember],
  );

  const handleBonusAmountChange = useCallback(
    e => {
      const value = e?.currentTarget?.value ?? e;

      if (e?.currentTarget) {
        setBonusAmount(() => value);
      } else {
        setBonusAmount(() => {
          if (!Object.keys(amountTypes).includes(value)) return Number(value);
          return Number(value) + Number(parseNumberFromString(bonusAmount));
        });
      }
      setBonusPercentAmount('');
      resetBonusRemember();
    },
    [bonusAmount, resetBonusRemember],
  );

  const handleBonusPercentChange = useCallback(
    e => {
      const value = String(e?.currentTarget?.value ?? e).replace('%', '');
      setBonusPercentAmount(value);
      setBonusAmount('');
      resetBonusRemember();
    },
    [resetBonusRemember],
  );

  const handleRememberBonusSelectionChange = useCallback(
    value => {
      setRememberBonusSelection(value);
      if (value) {
        dispatch(userSettingsActions.setSavedBonusSettings({ bonusAmount, bonusPercentAmount }));
      } else {
        dispatch(userSettingsActions.clearSavedBonusSettings());
      }
    },
    [dispatch, bonusAmount, bonusPercentAmount],
  );

  const isRememberBonusDisabled = !isBonus || (!bonusAmount && !bonusPercentAmount);

  return {
    players,
    amount,
    onSubmit,
    changeValue,
    handleIsBonusChange,
    handleBonusAmountChange,
    handleBonusPercentChange,
    handleRememberBonusSelectionChange,
    setAmount,
    currency,
    balance: authBalance,
    actionProcessing,
    locale,
    history,
    bonusAmount,
    bonusPercentAmount,
    isBonus,
    targetRole: userInfo.role,
    rememberBonusSelection,
    isRememberBonusDisabled,
    isDirectPlayer,
  };
};

export default useDepositModel;
